import dayjs, { Dayjs } from "dayjs"
import { MoodMeasurement } from "./types"
import { average } from "./stats"

export type ReportedMoodDay = {
  date: Dayjs
  enjoyment?: number
  growth?: number | null | undefined
  cravings?: number | null
}

export type MoodTrend = {
  direction: "increasing" | "decreasing" | "no_change"
  meanChange: number
}

/**
 * Compares one set of mood scores to another and returns the overall trend.
 * Returns null when either score set has no values to compare.
 * @param currentScores The current period's mood scores.
 * @param previousScores The comparison period's mood scores.
 */
export function getMoodTrend(
  currentScores: number[],
  previousScores: number[]
): MoodTrend | null {
  if (currentScores.length === 0 || previousScores.length === 0) {
    return null
  }

  const currentAverage = average(currentScores)
  const previousAverage = average(previousScores)
  const meanChange = currentAverage - previousAverage

  if (meanChange > 0) {
    return {
      direction: "increasing",
      meanChange,
    }
  }

  if (meanChange < 0) {
    return {
      direction: "decreasing",
      meanChange: Math.abs(meanChange),
    }
  }

  return {
    direction: "no_change",
    meanChange: 0,
  }
}

const getMoodMeasurementTimestamp = (
  measure: Pick<MoodMeasurement, "client_created_on" | "created_on">
) => dayjs(measure.client_created_on ?? measure.created_on)

/**
 * Returns the calendar day used as the chart's index for a given week offset.
 * Mood charts render reported days ending with the day before this index date.
 */
export const getMoodChartIndexDate = (weekOffset = 0) =>
  dayjs()
    .startOf("day")
    .subtract(weekOffset * 7, "day")

/**
 * Returns the day a mood response is describing rather than the day it was
 * submitted. The questionnaire asks about the prior day, so responses created
 * today are charted against yesterday.
 */
export const getMoodMeasurementReportedDate = (
  measure: Pick<MoodMeasurement, "client_created_on" | "created_on">
) => getMoodMeasurementTimestamp(measure).subtract(1, "day")

/**
 * Returns the reported-day range for the requested number of days before the
 * supplied index date. The index date itself is excluded.
 */
const getReportedDayRange = (
  indexDate: Dayjs,
  numberOfDays: number
): Dayjs[] => {
  const reportedDays = []
  for (let i = numberOfDays; i--; i > 0) {
    reportedDays.push(indexDate.startOf("day").subtract(i + 1, "days"))
  }
  return reportedDays
}

/**
 * Returns the latest submitted mood measurement for each reported day. This is
 * useful for charts that need stable reported-day buckets without enjoyment or
 * growth-specific normalization.
 */
const getLatestMoodMeasurementsByReportedDay = (
  moodMeasurements: MoodMeasurement[]
) =>
  moodMeasurements.reduce<Map<string, MoodMeasurement>>(
    (accumulator, measure) => {
      const reportedDayKey =
        getMoodMeasurementReportedDate(measure).format("YYYY-MM-DD")
      const existingMeasurement = accumulator.get(reportedDayKey)

      if (
        !existingMeasurement ||
        getMoodMeasurementTimestamp(measure).valueOf() >
          getMoodMeasurementTimestamp(existingMeasurement).valueOf()
      ) {
        accumulator.set(reportedDayKey, measure)
      }

      return accumulator
    },
    new Map()
  )

/**
 * Builds a complete reported-day timeline for mood charts, including missing
 * days, so weekly and monthly visualizations can render stable date buckets.
 * Enjoyment and growth are normalized from the stored 0-4 scale to the chart's
 * 1-5 display scale. Cravings is also normalized from its stored zero-indexed
 * scale to the chart's 1-5 display scale.
 */
export const getReportedMoodDays = (
  indexDate: Dayjs,
  numberOfDays: number,
  moodMeasurements: MoodMeasurement[]
) => {
  const reportedMeasurements =
    getLatestMoodMeasurementsByReportedDay(moodMeasurements)

  return getReportedDayRange(indexDate, numberOfDays).map((date) => {
    const measurement = reportedMeasurements.get(date.format("YYYY-MM-DD"))

    return {
      date,
      enjoyment: measurement ? measurement.enjoyment + 1 : undefined,
      growth:
        measurement &&
        measurement.growth !== null &&
        measurement.growth !== undefined
          ? measurement.growth + 1
          : undefined,
      cravings:
        measurement &&
        measurement.cravings !== null &&
        measurement.cravings !== undefined
          ? measurement.cravings + 1
          : undefined,
    }
  })
}
